import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#cleanupSwipeToClose();
    this.#clearClickSuppression();
    this.#teardownPopoverDocListeners();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #swipeStartY = null;
  #swipeStartTime = null;
  #isSwipeGesture = false;
  #swipeAbortController = null;
  /** @type {AbortController | null} */
  #swipeInlineAbortController = null;
  /** @type {AbortController | null} */
  #suppressClickAbort = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #suppressClickTimeout = null;
  /** @type {AbortController | null} */
  #popoverDocAbort = null;

  #isDialogPopover() {
    return this.hasAttribute('data-dialog-popover');
  }

  #teardownPopoverDocListeners() {
    this.#popoverDocAbort?.abort();
    this.#popoverDocAbort = null;
  }

  #popoverDocumentMousedown = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (this.#isDialogPopover()) {
      requestAnimationFrame(() => {
        if (dialog.open) return;
        dialog.show();
        this.dispatchEvent(new DialogOpenEvent());
        this.#teardownPopoverDocListeners();
        this.#popoverDocAbort = new AbortController();
        const signal = this.#popoverDocAbort.signal;
        document.addEventListener('keydown', this.#handleKeyDown, { signal });
        document.addEventListener('mousedown', this.#popoverDocumentMousedown, { capture: true, signal });
      });
      return;
    }

    const scrollY =
      document.body.style.position === 'fixed'
        ? Math.abs(parseInt(document.body.style.top, 10) || 0)
        : window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.#focusContainerOnOpen();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      // Add swipe-to-close for bottom drawers on mobile
      if (dialog.classList.contains('dialog-drawer--bottom') && isMobileBreakpoint()) {
        this.#initSwipeToClose();
      }

      // Zijwaartse swipe-to-close voor side drawers op mobiel (opt-in via
      // swipe-close-direction op de component, bv. de cart drawer).
      if (this.swipeCloseDirection && isMobileBreakpoint()) {
        this.#initSwipeToCloseInline();
      }
    });
  }

  /**
   * Moves focus to the dialog container itself on open, instead of letting the
   * native `showModal()` focus the first focusable child (e.g. the close
   * button). Opt-in via the `focus-container-on-open` attribute on the
   * component. Keeps the dialog accessible — it is labelled via
   * `aria-labelledby`, so screen readers still announce it, and Tab moves on to
   * the real controls — while avoiding a `:focus-visible` ring on the close
   * button when the dialog is opened via pointer/touch.
   */
  #focusContainerOnOpen() {
    if (!this.hasAttribute('focus-container-on-open')) return;

    const { dialog } = this.refs;
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    dialog.focus();
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (this.#isDialogPopover()) {
      this.#teardownPopoverDocListeners();
      if (dialog.open) {
        dialog.close();
        this.dispatchEvent(new DialogCloseEvent());
      }
      return;
    }

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#cleanupSwipeToClose();

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Closes the dialog from a dragged position with smooth fade animation.
   * The drawer continues moving in the drag direction while the backdrop fades out.
   * @param {number} currentTransform - The current transform value in pixels
   * @param {'x' | 'y'} [axis] - Drag axis: 'y' for bottom drawers, 'x' for side drawers
   */
  #closeFromDragPosition(currentTransform, axis = 'y') {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#cleanupSwipeToClose();

    // Calculate final position (move drawer completely off screen)
    const isHorizontal = axis === 'x';
    const drawerSize = isHorizontal
      ? dialog.offsetWidth || window.innerWidth
      : dialog.offsetHeight || window.innerHeight;
    const finalTransform = isHorizontal
      ? Math.sign(currentTransform || 1) * (drawerSize + 24)
      : drawerSize + Math.max(currentTransform, 50); // Ensure it moves off screen

    // Set transition for smooth animation
    dialog.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out';
    dialog.style.transform = isHorizontal
      ? `translateX(${finalTransform}px)`
      : `translateY(${finalTransform}px)`;

    if (isHorizontal) {
      // Geen `dialog-closing` erbij: die start een keyframe-animatie die de inline
      // transform zou overrulen (de drawer zou eerst terugspringen). De side drawer
      // schuift volledig van het scherm, dus alleen de backdrop faden we uit.
      dialog.getAnimations().forEach((animation) => animation.cancel());
      dialog.style.setProperty('--backdrop-opacity-dynamic', '0');
    } else {
      dialog.style.opacity = '0';
      dialog.classList.add('dialog-closing');
    }

    // Clean up after animation
    setTimeout(async () => {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
      dialog.classList.remove('dialog-closing');
      dialog.style.opacity = '';
      dialog.style.transform = '';
      dialog.style.transition = '';
      dialog.style.animation = '';
      dialog.style.removeProperty('--backdrop-opacity-dynamic');

      this.dispatchEvent(new DialogCloseEvent());
    }, 300);
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * The direction a side drawer may be swiped away in, or `null` when swipe-to-close
   * is off. Set via `swipe-close-direction="right|left"` on the component.
   *
   * @returns {'right' | 'left' | null}
   */
  get swipeCloseDirection() {
    const value = this.getAttribute('swipe-close-direction');
    return value === 'right' || value === 'left' ? value : null;
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }

  /**
   * Initializes drag functionality for bottom drawers (both up and down).
   * Works specifically from the drag handle area at the top of the drawer.
   */
  #initSwipeToClose() {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#swipeAbortController = new AbortController();
    const signal = this.#swipeAbortController.signal;

    const DRAG_HANDLE_AREA = 60; // Area from top where drag handle is (matches CSS)
    const DRAG_THRESHOLD = 3; // Minimum movement to start drag
    const CLOSE_THRESHOLD = 150; // Distance to close drawer when dragging down
    const VELOCITY_THRESHOLD = 0.3; // Velocity threshold for closing
    const MAX_UPWARD_DRAG = 100; // Maximum upward drag distance

    let initialY = 0;
    let initialTransform = 0;
    let isDragging = false;
    let startedInDragHandle = false;

    const isInDragHandleArea = (touchY) => {
      const drawerRect = dialog.getBoundingClientRect();
      const relativeY = touchY - drawerRect.top;
      const inArea = relativeY >= 0 && relativeY <= DRAG_HANDLE_AREA;
      return inArea;
    };

    const handleTouchStart = (e) => {
      // Don't interfere with clicks on interactive elements
      const target = e.target;
      if (target && (
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a, button, [role="button"], [onclick]')
      )) {
        // Allow normal interaction with links and buttons
        return;
      }

      const touch = e.touches[0];
      if (!touch) {
        return;
      }

      // Check if touch is within the drawer bounds
      const drawerRect = dialog.getBoundingClientRect();
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      
      // Only proceed if touch is within drawer bounds
      if (touchX < drawerRect.left || touchX > drawerRect.right || 
          touchY < drawerRect.top || touchY > drawerRect.bottom) {
        return;
      }

      // Check if touch is in drag handle area (top 60px of drawer)
      startedInDragHandle = isInDragHandleArea(touchY);

      // Only allow dragging if started in drag handle area
      if (!startedInDragHandle) {
        return;
      }

      // Start tracking only if in drag handle area or drawer is already transformed
      this.#swipeStartY = touchY;
      this.#swipeStartTime = performance.now();
      initialY = touchY;
      
      // Get current transform value if any
      const currentTransform = dialog.style.transform;
      if (currentTransform) {
        const match = currentTransform.match(/translateY\(([^)]+)px\)/);
        initialTransform = match ? parseFloat(match[1]) || 0 : 0;
      } else {
        initialTransform = 0;
      }

      isDragging = false;
    };

    const handleTouchMove = (e) => {
      if (this.#swipeStartY === null) return;

      const touch = e.touches[0];
      if (!touch) return;

      const currentY = touch.clientY;
      const deltaY = currentY - this.#swipeStartY;
      const scrollTop = dialog.scrollTop;

      // Only start dragging if started in drag handle area
      if (!isDragging && Math.abs(deltaY) > DRAG_THRESHOLD) {
        // Only allow drag if started in drag handle area
        if (startedInDragHandle) {
          isDragging = true;
          // Disable all transitions and animations to allow manual transform
          dialog.style.transition = 'none';
          dialog.style.animation = 'none';
          dialog.style.animationName = 'none';
          dialog.classList.add('drawer-dragging');
          e.preventDefault();
          e.stopPropagation();
        } else {
          // Not dragging, allow normal interaction (clicks, scrolls)
          this.#swipeStartY = null;
          return;
        }
      }

      // Apply drag transform
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();

        // Calculate new transform value
        let newTransform = initialTransform + deltaY;
        
        // Limit upward drag
        if (newTransform < -MAX_UPWARD_DRAG) {
          newTransform = -MAX_UPWARD_DRAG;
        }
        
        // Allow downward drag without limit (for closing)
        // Apply transform (animation is disabled via drawer-dragging class)
        dialog.style.transform = `translateY(${newTransform}px)`;

        // Update backdrop opacity based on downward drag only
        if (newTransform > 0) {
          const maxHeight = dialog.offsetHeight || window.innerHeight;
          const opacity = Math.max(0, 1 - (newTransform / maxHeight) * 1.5);
          dialog.style.setProperty('--backdrop-opacity-dynamic', opacity.toString());
        } else {
          // Reset backdrop opacity when dragging up
          dialog.style.setProperty('--backdrop-opacity-dynamic', '1');
        }

      }
    };

    const handleTouchEnd = (e) => {
      if (this.#swipeStartY === null) {
        return;
      }

      const touch = e.changedTouches[0];
      if (!touch) {
        return;
      }

      const currentY = touch.clientY;
      const deltaY = currentY - this.#swipeStartY;
      const timeDelta = performance.now() - (this.#swipeStartTime || 0);
      const velocity = timeDelta > 0 ? Math.abs(deltaY / timeDelta) : 0;

      // Get current transform
      const currentTransform = initialTransform + deltaY;

      // Remove dragging class to allow transitions
      dialog.classList.remove('drawer-dragging');

      if (isDragging) {
        // Close if dragged down enough or with sufficient downward velocity
        if (currentTransform > CLOSE_THRESHOLD || (deltaY > 0 && velocity > VELOCITY_THRESHOLD && currentTransform > 50)) {
          // Close from current position - continue moving down while fading out
          this.#closeFromDragPosition(currentTransform);
        } else {
          // Snap back smoothly to original position with animation
          dialog.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out';
          dialog.style.transform = '';
          dialog.style.removeProperty('--backdrop-opacity-dynamic');
          
          // Reset transition after animation completes
          setTimeout(() => {
            dialog.style.transition = '';
          }, 300);
        }
      } else {
        // Restore transition if not dragging
        dialog.style.transition = '';
      }

      this.#swipeStartY = null;
      this.#swipeStartTime = null;
      isDragging = false;
      startedInDragHandle = false;
      initialTransform = 0;
    };

    // touchstart is passive to allow normal clicks/scrolls
    // touchmove is non-passive only when we need to prevent default (during drag)
    dialog.addEventListener('touchstart', handleTouchStart, { passive: true, signal });
    dialog.addEventListener('touchmove', handleTouchMove, { passive: false, signal });
    dialog.addEventListener('touchend', handleTouchEnd, { passive: true, signal });
    dialog.addEventListener('touchcancel', handleTouchEnd, { passive: true, signal });
  }

  /**
   * Swipe-to-close voor side drawers op mobiel: de gebruiker sleept de drawer
   * zijwaarts weg (zie `swipe-close-direction`). Verticaal scrollen in de drawer
   * blijft werken — een gesture geldt alleen als sleep zodra de horizontale
   * beweging groter is dan de verticale.
   */
  #initSwipeToCloseInline() {
    const { dialog } = this.refs;
    const direction = this.swipeCloseDirection;
    if (!dialog || !direction) return;

    this.#swipeInlineAbortController = new AbortController();
    const { signal } = this.#swipeInlineAbortController;

    const DRAG_THRESHOLD = 10; // px beweging voordat het een sleep is
    const CLOSE_RATIO = 0.3; // deel van de drawerbreedte waarna hij dichtgaat
    const CLOSE_DISTANCE_MAX = 120; // bovengrens voor die afstand
    const VELOCITY_THRESHOLD = 0.4; // px/ms — een flick sluit ook bij korte afstand
    const FLICK_MIN_DISTANCE = 20; // minimale afstand bij een flick
    const SNAP_BACK_DURATION = 250;

    /** 1 als de drawer naar rechts wegschuift, -1 naar links. */
    const sign = direction === 'right' ? 1 : -1;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let tracking = false;
    let dragging = false;
    let offset = 0;

    /**
     * Sleep niet oppakken als de vinger in iets zit dat zélf nog horizontaal kan
     * scrollen in de swipe-richting (bv. een carrousel in de drawer). Let op:
     * `overflow-y: auto` maakt de computed `overflow-x` ook `auto`, dus de
     * scrollpositie is de echte test.
     * @param {EventTarget | null} target
     * @returns {boolean}
     */
    const startsInHorizontalScroller = (target) => {
      let node = target instanceof Element ? target : null;

      while (node && node !== dialog) {
        const maxScrollLeft = node.scrollWidth - node.clientWidth;

        if (maxScrollLeft > 1) {
          const { overflowX } = getComputedStyle(node);
          const scrollable = overflowX === 'auto' || overflowX === 'scroll';
          const canScrollFurther = sign === 1 ? node.scrollLeft > 1 : node.scrollLeft < maxScrollLeft - 1;

          if (scrollable && canScrollFurther) return true;
        }

        node = node.parentElement;
      }

      return false;
    };

    const reset = () => {
      tracking = false;
      dragging = false;
      offset = 0;
    };

    /** Laat de drawer terugveren naar de open positie. */
    const snapBack = () => {
      dialog.classList.remove('drawer-dragging');
      dialog.style.transition = `transform ${SNAP_BACK_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      dialog.style.transform = `translateX(${offset}px)`;
      requestAnimationFrame(() => {
        dialog.style.transform = 'translateX(0px)';
      });
      dialog.style.removeProperty('--backdrop-opacity-dynamic');
      setTimeout(() => {
        dialog.style.transition = '';
        dialog.style.transform = '';
      }, SNAP_BACK_DURATION);
    };

    /** @param {TouchEvent} event */
    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        // Tweede vinger erbij (bv. pinch): sleep afbreken, niet halverwege blijven hangen.
        if (dragging) snapBack();
        reset();
        return;
      }

      const touch = event.touches[0];
      if (!touch || startsInHorizontalScroller(event.target)) return;

      startX = touch.clientX;
      startY = touch.clientY;
      startTime = performance.now();
      tracking = true;
      dragging = false;
      offset = 0;
    };

    /** @param {TouchEvent} event */
    const handleTouchMove = (event) => {
      if (!tracking) return;

      const touch = event.touches[0];
      if (!touch) return;

      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      if (!dragging) {
        // Verticaal scrollen of een tik mag nooit als sleep gelden.
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          tracking = false;
          return;
        }
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
        // Alleen in de sluitrichting slepen.
        if (Math.sign(deltaX) !== sign) {
          tracking = false;
          return;
        }

        dragging = true;
        // De (afgeronde) open-animatie vult met `forwards` de transform en zou de
        // inline transform overrulen. Cancel 'm, dan kan een latere close-animatie
        // wél weer starten — anders sluit de X-knop straks zonder animatie.
        dialog.getAnimations().forEach((animation) => animation.cancel());
        dialog.style.transition = 'none';
        dialog.classList.add('drawer-dragging');
      }

      event.preventDefault();

      offset = sign * Math.max(0, sign * deltaX);
      dialog.style.transform = `translateX(${offset}px)`;

      const width = dialog.offsetWidth || window.innerWidth;
      const progress = Math.min(1, Math.abs(offset) / width);
      dialog.style.setProperty('--backdrop-opacity-dynamic', Math.max(0, 1 - progress * 1.5).toString());
    };

    /** @param {TouchEvent} event */
    const handleTouchEnd = (event) => {
      if (!dragging) {
        reset();
        return;
      }

      const touch = event.changedTouches[0];
      const travelled = touch ? sign * (touch.clientX - startX) : sign * offset;
      const elapsed = performance.now() - startTime;
      const velocity = elapsed > 0 ? travelled / elapsed : 0;
      const width = dialog.offsetWidth || window.innerWidth;
      const closeDistance = Math.min(width * CLOSE_RATIO, CLOSE_DISTANCE_MAX);

      // Een sleep die op een knop of link begon mag geen klik afvuren.
      this.#suppressClickAfterDrag();

      const farEnough = travelled > closeDistance;
      const flicked = velocity > VELOCITY_THRESHOLD && travelled > FLICK_MIN_DISTANCE;

      // Bij touchcancel (systeem neemt over) altijd terugveren, nooit sluiten.
      if (event.type !== 'touchcancel' && (farEnough || flicked)) {
        this.#closeFromDragPosition(offset, 'x');
      } else {
        snapBack();
      }

      reset();
    };

    dialog.addEventListener('touchstart', handleTouchStart, { passive: true, signal });
    dialog.addEventListener('touchmove', handleTouchMove, { passive: false, signal });
    dialog.addEventListener('touchend', handleTouchEnd, { passive: true, signal });
    dialog.addEventListener('touchcancel', handleTouchEnd, { passive: true, signal });
  }

  /**
   * Slikt de eerstvolgende klik in: browsers vuren na een sleep alsnog een klik af
   * op de knop of link waar de vinger begon. Hangt aan `document` (niet aan de
   * dialog) omdat de drawer op dat moment al aan het sluiten is.
   */
  #suppressClickAfterDrag() {
    this.#clearClickSuppression();

    this.#suppressClickAbort = new AbortController();

    document.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.#clearClickSuppression();
      },
      { capture: true, signal: this.#suppressClickAbort.signal }
    );

    this.#suppressClickTimeout = setTimeout(() => this.#clearClickSuppression(), 400);
  }

  #clearClickSuppression() {
    if (this.#suppressClickTimeout) {
      clearTimeout(this.#suppressClickTimeout);
      this.#suppressClickTimeout = null;
    }

    this.#suppressClickAbort?.abort();
    this.#suppressClickAbort = null;
  }

  /**
   * Cleans up drag-to-close event listeners.
   */
  #cleanupSwipeToClose() {
    if (this.#swipeAbortController) {
      this.#swipeAbortController.abort();
      this.#swipeAbortController = null;
    }

    if (this.#swipeInlineAbortController) {
      this.#swipeInlineAbortController.abort();
      this.#swipeInlineAbortController = null;
    }

    const { dialog } = this.refs;
    if (dialog) {
      dialog.style.transform = '';
      dialog.style.transition = '';
      dialog.style.animation = '';
      dialog.style.removeProperty('--backdrop-opacity-dynamic');
      dialog.classList.remove('drawer-dragging');
    }

    this.#swipeStartY = null;
    this.#swipeStartTime = null;
    this.#isSwipeGesture = false;
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
